import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { lightFormat } from 'date-fns';

import { AccountingApi } from '_api';
import { FIELD_KEYS } from '_constants/forms';
import { handleError, handleResponse } from '_helpers/api';
import { saveFile } from '_helpers/file';

import { selectParams } from './selector';

export const getFiles = createAsyncThunk(
  'files/getFiles',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const params = selectParams(getState());

      const response = await AccountingApi.getFiles({
        customerId,
        productId,
        ...params,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const downloadFile = createAsyncThunk(
  'files/downloadFile',
  async function ({ customerId, productId, fileId, fileName }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.downloadFile({
        customerId,
        productId,
        fileId,
      });

      const data = handleResponse(response);
      saveFile(data, fileName);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const initialForm = {};

export const initialParams = {
  [FIELD_KEYS.page]: 1,
  [FIELD_KEYS.count]: 2,
  [FIELD_KEYS.startDateFrom]: null,
  [FIELD_KEYS.startDateTo]: null,
  [FIELD_KEYS.endDateFrom]: null,
  [FIELD_KEYS.endDateTo]: null,
  [FIELD_KEYS.type]: null,
  [FIELD_KEYS.year]: null,
};

const filesSlice = createSlice({
  name: 'files',
  initialState: {
    form: initialForm,
    params: initialParams,
    data: {},
    error: null,
    fetchCounter: 0,
    loading: false,
    downloading: false,
  },
  reducers: {
    refreshForm(state, action) {
      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    resetForm(state) {
      state.form = {
        ...initialForm,
      };
    },
    refreshParams(state, action) {
      state.params = {
        ...state.params,
        ...action.payload,
        [FIELD_KEYS.year]: action.payload[FIELD_KEYS.year]
          ? lightFormat(action.payload[FIELD_KEYS.year], 'yyyy')
          : action.payload[FIELD_KEYS.year] === null
          ? null
          : state.params[FIELD_KEYS.year],
      };
    },
    resetData(state) {
      state.data = {};
    },
    resetError(state) {
      state.error = null;
    },
    resetFetchCounter(state) {
      state.fetchCounter = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFiles.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getFiles.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.fetchCounter += 1;
      })
      .addCase(getFiles.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(downloadFile.pending, (state) => {
        state.error = null;
        state.downloading = true;
      })
      .addCase(downloadFile.fulfilled, (state) => {
        state.downloading = false;
      })
      .addCase(downloadFile.rejected, (state, action) => {
        state.error = action.payload;
        state.downloading = false;
      });
  },
});

export const { refreshForm, resetForm, refreshParams, resetData, resetError, resetFetchCounter } = filesSlice.actions;

export default filesSlice.reducer;
